/** SUNTRONIX ONE — Renewal Alerts workspace (Phase 1). */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCcw, Clock, ArrowRight, Mail, X, Download } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import DigestSendingCalendar from "@/components/app/DigestSendingCalendar";

const BUCKET_TONE = {
  EXPIRED:      "bg-rose-100 text-rose-800 border-rose-300",
  WITHIN_7D:    "bg-rose-50 text-rose-700 border-rose-200",
  WITHIN_15D:   "bg-amber-100 text-amber-900 border-amber-200",
  WITHIN_30D:   "bg-amber-50 text-amber-800 border-amber-200",
  WITHIN_60D:   "bg-sky-50 text-sky-700 border-sky-200",
  WITHIN_90D:   "bg-slate-100 text-slate-700 border-slate-200",
};
const BUCKET_LABEL = {
  EXPIRED:    "Expired",
  WITHIN_7D:  "≤ 7 days",
  WITHIN_15D: "≤ 15 days",
  WITHIN_30D: "≤ 30 days",
  WITHIN_60D: "≤ 60 days",
  WITHIN_90D: "≤ 90 days",
};


export default function Renewals() {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], counts: {}, total: 0 });
  const [busy, setBusy] = useState(false);
  const [bucket, setBucket] = useState("ALL");
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/renewals/alerts", { params: { horizon_days: 90 } });
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setBusy(false); }
  }, []);

  const previewDigest = useCallback(async () => {
    setPreviewBusy(true);
    try {
      const { data } = await api.post("/renewals/digest/preview");
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally { setPreviewBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = bucket === "ALL"
    ? data.items
    : data.items.filter((i) => i.bucket === bucket);

  return (
    <div className="space-y-6 max-w-[1600px]" data-testid="renewals-page">
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">
            My Work · Coverage
          </div>
          <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1 flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-slate-700" strokeWidth={1.6} />
            Renewal Alerts
          </h1>
          <p className="text-sm text-slate-600 mt-1.5 max-w-2xl">
            AppleCare agreements, device coverage and insurance policies expiring in the next
            90 days — or already expired. One-tap renew per row.
          </p>
        </div>
        <Button variant="outline" onClick={previewDigest} disabled={previewBusy} className="h-9"
          data-testid="ren-preview-digest">
          <Mail className={"h-4 w-4 mr-1.5 " + (previewBusy ? "animate-pulse" : "")} />
          Preview this week's email
        </Button>
        <Button variant="outline" onClick={load} disabled={busy} className="h-9" data-testid="ren-reload">
          <RefreshCcw className={"h-4 w-4 mr-1.5 " + (busy ? "animate-spin" : "")} />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Kpi label="Total" value={data.total || 0} bucket="ALL" onClick={() => setBucket("ALL")} active={bucket === "ALL"} />
        {["EXPIRED", "WITHIN_7D", "WITHIN_15D", "WITHIN_30D", "WITHIN_60D"].map((b) => (
          <Kpi key={b} label={BUCKET_LABEL[b]} value={data.counts?.[b] || 0}
            bucket={b} tone={BUCKET_TONE[b]}
            onClick={() => setBucket(b)} active={bucket === b} />
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">Coverage</th>
              <th className="text-left px-3 py-2">Reference</th>
              <th className="text-left px-3 py-2">Expires</th>
              <th className="text-left px-3 py-2">Bucket</th>
              <th className="text-right px-3 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-8 text-slate-500">
                {busy ? "Loading…" : bucket === "ALL"
                  ? "No coverage expiring in the next 90 days — you're all clear."
                  : `No items in ${BUCKET_LABEL[bucket]}.`}
              </td></tr>
            ) : filtered.map((r) => (
              <tr key={`${r.kind}-${r.id}`} data-testid={`ren-row-${r.id}`} className="hover:bg-slate-50">
                <td className="px-3 py-2">
                  <div className="text-[13px] font-medium text-slate-900">{r.name}</div>
                  <div className="text-[10px] uppercase tracking-[0.1em] text-slate-500">
                    {(r.kind || "").replace(/_/g, " ")}
                    {r.plan_type && ` · ${r.plan_type.replace(/_/g, " ")}`}
                    {r.provider && ` · ${r.provider}`}
                  </div>
                </td>
                <td className="px-3 py-2 font-mono text-[12px] text-slate-700">{r.ref || "—"}</td>
                <td className="px-3 py-2 text-[12px] text-slate-700">
                  <Clock className="h-3.5 w-3.5 inline mr-1 text-slate-400" />
                  {r.expires} <span className="text-slate-400">·</span>{" "}
                  <span className={r.days_left < 0 ? "text-rose-700 font-medium" : "text-slate-600"}>
                    {r.days_left < 0 ? `${Math.abs(r.days_left)}d ago` : `in ${r.days_left}d`}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <span className={"h-5 px-2 rounded-full border text-[10px] font-medium uppercase tracking-[0.1em] " +
                    (BUCKET_TONE[r.bucket] || "bg-slate-100 text-slate-700")}>
                    {BUCKET_LABEL[r.bucket] || r.bucket}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  {r.renew_action?.url ? (
                    <Link to={r.renew_action.url}>
                      <Button size="sm" className="h-8 bg-slate-900 text-white hover:bg-slate-800"
                        data-testid={`ren-renew-${r.id}`}>
                        {r.renew_action.label || "Renew"}
                        <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                      </Button>
                    </Link>
                  ) : (
                    <Button size="sm" variant="outline" className="h-8" disabled>Renew</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {preview && <DigestPreviewDrawer preview={preview} onClose={() => setPreview(null)} />}

      <DigestSendingCalendar
        masterCompanyId={user?.master_company_id}
        canPickTenant={(user?.role || "").startsWith("suntronix") || user?.role === "super_admin"}
      />
    </div>
  );
}


function Kpi({ label, value, tone, onClick, active }) {
  return (
    <button onClick={onClick}
      className={"text-left border rounded-md p-3 hover:shadow-sm transition-all " +
        (active ? "border-slate-900 ring-1 ring-slate-900" : "border-slate-200 bg-white") +
        " " + (tone || "")}>
      <div className="text-[10px] uppercase tracking-[0.14em]">{label}</div>
      <div className="text-2xl font-heading font-semibold mt-1">{value}</div>
    </button>
  );
}


function DigestPreviewDrawer({ preview, onClose }) {
  const [subjectTpl, setSubjectTpl] = useState(preview?.settings?.subject_template || "");
  const [escalation, setEscalation] = useState(preview?.settings?.escalation_contact || "");
  const [cadence, setCadence] = useState(preview?.settings?.cadence || "weekly");
  const [savingSettings, setSavingSettings] = useState(false);
  const [localPreview, setLocalPreview] = useState(preview);
  const mcId = preview.master_company_id;

  const saveAndReload = useCallback(async () => {
    setSavingSettings(true);
    try {
      await api.put(`/renewals/digest/settings?master_company_id=${mcId}`, {
        subject_template: subjectTpl || null,
        escalation_contact: escalation || null,
        cadence,
      });
      const { data } = await api.post(`/renewals/digest/preview?master_company_id=${mcId}`);
      setLocalPreview(data);
      toast.success("Digest settings saved · preview refreshed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save settings");
    } finally { setSavingSettings(false); }
  }, [mcId, subjectTpl, escalation, cadence]);

  const p = localPreview;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end"
      onClick={onClose} data-testid="ren-preview-drawer">
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-white h-full overflow-auto p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">
              Renewal Digest · Preview
            </div>
            <h2 className="text-2xl font-heading font-semibold text-slate-900 mt-1">
              {p.master_company_name}
            </h2>
            <div className="text-[12px] text-slate-500 mt-0.5">
              {p.alert_count} alert(s) · horizon {p.settings?.horizon_days || 30}d
              · cadence {p.settings?.cadence || "weekly"}
            </div>
          </div>
          <button onClick={onClose}
            className="text-slate-400 hover:text-slate-800"
            data-testid="ren-preview-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <Fact label="Would send now" value={p.would_send ? "Yes" : "No"}
            tone={p.would_send ? "ok" : "warn"} testId="ren-preview-would-send" />
          <Fact label="Recipients"
            value={p.recipients?.length ? p.recipients.join(", ") : "None resolved"}
            tone={p.recipients?.length ? "ok" : "warn"} testId="ren-preview-recipients" />
        </div>

        <div className="mt-5">
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mb-2">Buckets</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(p.buckets || {}).map(([k, v]) => (
              <span key={k} className="text-[11px] px-2 py-1 rounded border border-slate-200 bg-slate-50">
                {k.replace(/_/g, " ")}: <span className="font-semibold">{v}</span>
              </span>
            ))}
            {Object.keys(p.buckets || {}).length === 0 && (
              <span className="text-[12px] text-slate-500">No items in horizon.</span>
            )}
          </div>
        </div>

        <div className="mt-5 border rounded-md p-3 bg-slate-50 space-y-3">
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-600">Personalisation</div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5 block">
              Subject template
            </label>
            <input value={subjectTpl} onChange={(e) => setSubjectTpl(e.target.value)}
              placeholder="Renewal digest · {alerts} item(s) · {customer}"
              data-testid="ren-preview-subject-tpl"
              className="w-full border border-slate-200 rounded px-2 h-9 text-[13px]" />
            <div className="text-[10px] text-slate-500 mt-1">
              Placeholders: <code className="bg-white px-1 rounded">{'{alerts}'}</code>{" "}
              <code className="bg-white px-1 rounded">{'{customer}'}</code>{" "}
              <code className="bg-white px-1 rounded">{'{horizon}'}</code>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5 block">
                Escalation contact
              </label>
              <input value={escalation} onChange={(e) => setEscalation(e.target.value)}
                placeholder="ops@example.com or +91-…"
                data-testid="ren-preview-escalation"
                className="w-full border border-slate-200 rounded px-2 h-9 text-[13px]" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5 block">
                Cadence
              </label>
              <select value={cadence} onChange={(e) => setCadence(e.target.value)}
                data-testid="ren-preview-cadence"
                className="w-full border border-slate-200 rounded px-2 h-9 text-[13px] bg-white">
                <option value="weekly">Weekly (Mondays)</option>
                <option value="daily">Daily</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end">
            <Button size="sm" onClick={saveAndReload} disabled={savingSettings}
              data-testid="ren-preview-save-settings"
              className="h-8 bg-slate-900 text-white hover:bg-slate-800">
              {savingSettings ? "Saving…" : "Save & refresh preview"}
            </Button>
          </div>
        </div>

        <div className="mt-5">
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mb-2">Subject</div>
          <div className="border border-slate-200 rounded-md p-3 text-[13px] font-medium bg-slate-50"
            data-testid="ren-preview-subject">
            {p.subject}
          </div>
        </div>

        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mb-2">Body</div>
          <pre className="border border-slate-200 rounded-md p-3 text-[12px] leading-relaxed whitespace-pre-wrap font-mono text-slate-800 bg-white"
            data-testid="ren-preview-body">
{p.body}
          </pre>
        </div>

        <div className="mt-5 flex items-center justify-between border-t pt-3">
          <div className="text-[11px] text-slate-500 pr-4">
            This is exactly what Suntronix ONE will email your IT contacts every Monday 09:00 UTC.
          </div>
          <a
            href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/renewals/digest/csv`}
            target="_blank" rel="noreferrer"
            data-testid="ren-preview-csv"
            className="inline-flex items-center gap-1.5 text-[12px] font-medium h-9 px-3 rounded-md border border-slate-200 hover:bg-slate-50 whitespace-nowrap">
            <Download className="h-3.5 w-3.5" /> Download CSV
          </a>
        </div>
      </div>
    </div>
  );
}


function Fact({ label, value, tone, testId }) {
  const tint = tone === "ok" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
             : tone === "warn" ? "text-amber-800 bg-amber-50 border-amber-200"
             : "text-slate-700 bg-white border-slate-200";
  return (
    <div className={"border rounded-md p-2.5 " + tint} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.14em] opacity-70">{label}</div>
      <div className="text-[13px] mt-0.5 font-medium break-words">{value}</div>
    </div>
  );
}
